import { CenterPage } from "Components/CenterPage/CenterPage";
import Loading from "Components/Loading/Loading";
import LightningLandersBuilder from "features/lightningLanders/components/LightningLandersBuilder";
import produce from "immer";
import { useCallback, useEffect, useRef } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  saveSharedComponentBlock,
  readSharedComponentBlock,
  useSharedComponentBlocks,
} from "features/sharedComponentBlocks";
import { friendlyDateTime } from "Helpers/dates";
import LightningLanderProvider, {
  useLightningLanderContext,
} from "features/lightningLanders/providers/LightningLanderProvider";
import {
  LightningLanderThemeProvider,
} from "features/lightningLanders/providers/LightningLanderThemeProvider";
import { ApiSimpleComponent } from "@incendium/api";
import { useSelectedProject } from "Hooks";

function SharedComponentBlockPageInner() {
  const { componentId } = useParams<{ componentId: string }>();
  const { selectedProject } = useSelectedProject();
  const { setBlocks } = useSharedComponentBlocks();
  const history = useHistory();
  const location = useLocation();
  const { component, setComponent } = useLightningLanderContext();
  const creationStarted = useRef(false);

  const onEditSaved = useCallback(
    (component: ApiSimpleComponent) => {
      setComponent(component);
      setBlocks((components) =>
        produce(components, (draft) => {
          if (!component) {
            return;
          }
          // Use externalId for shared blocks, fallback to id for comparison
          const componentKey = component.externalId || component.id?.toString();
          const idx = draft.findIndex((d) => (d.externalId || d.id?.toString()) === componentKey);
          if (idx >= 0) {
            draft[idx] = component;
          }
        })
      );
    },
    [setBlocks, setComponent]
  );

  useEffect(() => {
    // Check if component is already loaded and matches the current componentId
    const componentKey = component.externalId || component.id?.toString();
    if (componentKey && componentKey === componentId) {
      return;
    }
    // Prevent multiple creations for /new
    if (componentId === "new" && creationStarted.current) {
      return;
    }

    if (componentId === "new") {
      creationStarted.current = true;
      const createBlock = async () => {
        const res = await saveSharedComponentBlock({
          name: `Shared Block (${friendlyDateTime(new Date())})`,
        });
        // Set component immediately to prevent re-creation
        setComponent(res.component as ApiSimpleComponent);
        setBlocks((components) =>
          produce(components, (draft) => {
            draft.push(res.component!);
          })
        );
        // Use externalId for shared blocks
        const newBlockId = res.component?.externalId || res.component?.id?.toString();
        const newPath = location.pathname.replace(/\/new$/, `/${newBlockId}`);
        history.push(newPath);
      };
      createBlock();
    } else {
      const readBlock = async () => {
        // componentId is already a string (MongoDB ObjectID)
        const cRes = await readSharedComponentBlock(componentId);
        // Shared blocks don't have theme, so skip theme parsing
        setComponent(cRes.component as ApiSimpleComponent);
      };
      readBlock();
    }
  }, [
    history,
    location,
    componentId,
    setComponent,
    setBlocks,
    component.externalId,
    component.id,
  ]);

  // Check if component is loaded (using externalId or id)
  if (!component?.externalId && !component?.id) {
    return (
      <CenterPage calcHeight>
        <Loading />
      </CenterPage>
    );
  }

  // Use a dummy project for the builder - it needs a project object but we'll use custom save function
  const dummyProject = selectedProject || { id: 0, name: "" };

  return (
    <LightningLandersBuilder
      saveToken={null}
      project={dummyProject}
      onSaved={onEditSaved}
      customSaveFunction={async (component: ApiSimpleComponent, token?: string) => {
        const res = await saveSharedComponentBlock(component, token);
        return res;
      }}
    />
  );
}

function SharedComponentBlockPage() {
  return (
    <LightningLanderProvider>
      <LightningLanderThemeProvider>
        <SharedComponentBlockPageInner />
      </LightningLanderThemeProvider>
    </LightningLanderProvider>
  );
}

export default SharedComponentBlockPage;

